import {track} from './journeyEvents';
import axios from 'axios';

import { API_BASE_URL } from './apiConfig';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: string;
  };
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  full_name?: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
}

class AuthService {
  async register(data: RegisterRequest): Promise<void> {
    try {
      track('auth_started',{surface:'auth',cta:'register'});
      const visitor=localStorage.getItem('ninaCaptureSession');
      await api.post('/api/auth/register',data,visitor ? {headers:{'X-Capture-Session':visitor}} : undefined);
    } catch (error: any) {
      // Handle validation errors (422)
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const details = error.response.data.detail;
        
        // Check if it's an array of validation errors
        if (Array.isArray(details)) {
          const firstError = details[0];
          if (firstError.msg) {
            throw new Error(firstError.msg);
          }
          if (firstError.ctx?.reason) {
            throw new Error(firstError.ctx.reason);
          }
        }
        
        // If detail is a string
        if (typeof details === 'string') {
          throw new Error(details);
        }
      }
      
      // Handle other errors
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      throw new Error(error.message || 'Registration failed');
    }
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const eid=track('auth_started',{surface:'auth',cta:'login'});
      const response = await api.post<LoginResponse>('/api/auth/login', credentials,eid?{headers:{'X-Journey-Event':eid}}:undefined);
      
      const token = response.data.access_token;
      
      localStorage.setItem('token', token);
      const visitor=localStorage.getItem('ninaCaptureSession');
      if(visitor){
        await api.post('/api/capture-session/link',{}, {headers:{Authorization:`Bearer ${token}`,'X-Capture-Session':visitor}});
        localStorage.removeItem('ninaCaptureSession');
      }
      
      
      // Dispatch custom event to notify SubscriptionContext
      window.dispatchEvent(new Event('loginSuccess'));
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors (422)
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const details = error.response.data.detail;
        
        // Check if it's an array of validation errors
        if (Array.isArray(details)) {
          const firstError = details[0];
          if (firstError.msg) {
            throw new Error(firstError.msg);
          }
          if (firstError.ctx?.reason) {
            throw new Error(firstError.ctx.reason);
          }
        }
        
        // If detail is a string
        if (typeof details === 'string') {
          throw new Error(details);
        }
      }
      
      // Handle other errors
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      throw new Error(error.message || 'Login failed');
    }
  }

  logout(): void {
    localStorage.removeItem('token');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async getCurrentUser(): Promise<User> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await api.get<User>('/api/users/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }
}

export const authService = new AuthService();
